import type { FC } from "react";

import styled from "styled-components";

import type { IconName } from "@similarweb/icons";
import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { ChangeValue, Tooltip } from "@similarweb/ui-components";

import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { changeFilter } from "app/filters/ngFilters";

const { carbon, orange, blue } = colorsPalettes;

const LoaderContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    width: 100%;
`;

export const CubeWrapper = styled.div<{ isClickable?: boolean }>`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 12px;
    justify-content: space-between;
    box-sizing: border-box;
    border-radius: 4px;
    background-color: ${carbon[25]};
    transition: background-color ease-in-out 300ms;
    cursor: ${({ isClickable }) => `${isClickable ? "pointer" : "default"}`};
    &:hover {
        background-color: ${carbon[50]};
    }
`;

const TopContainer = styled.div`
    display: flex;
    align-content: center;
    gap: 4px;
    justify-content: space-between;
`;

const TitleContainer = styled.span`
    ${setFontVariant("subTitle3Alt", carbon[400])};
`;

const StyledIcon = styled(SWReactIcons)`
    svg {
        width: 20px;
        height: 20px;
        path {
            fill: ${carbon[200]};
        }
    }
`;

const ValContainer = styled.span`
    ${setFontVariant("h4", carbon[400])};
`;

// Grid layout for PoP data: values on left, change on bottom right
const PopGridContainer = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    grid-template-areas:
        "prev-value empty"
        "curr-value change";
    align-items: end;
    gap: 4px;
`;

const PreviousValueCell = styled.div`
    grid-area: prev-value;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const CurrentValueCell = styled.div`
    grid-area: curr-value;
    display: flex;
    align-items: center;
    gap: 6px;
`;

const ChangeCell = styled.div`
    grid-area: change;
    display: flex;
    align-items: flex-end;
    justify-content: flex-end;
`;

const ColorDot = styled.span<{ color: string }>`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${({ color }) => color};
    flex-shrink: 0;
`;

const PopValueText = styled.span`
    ${setFontVariant("tab", carbon[500])};
`;

interface ICubeProps {
    title: string;
    tooltip: string;
    iconName: IconName;
    valNum: string;
    isLoading?: boolean;
    previousValue?: string;
    change?: number;
}

const Cube = ({ title, tooltip, iconName, valNum, isLoading, previousValue, change }: ICubeProps) => {
    const hasPopData = previousValue !== undefined && change !== undefined;

    if (isLoading) {
        return (
            <LoaderContainer>
                <LoadingSpinner />
            </LoaderContainer>
        );
    }

    return (
        <Tooltip content={tooltip}>
            <CubeWrapper data-automation="Cube">
                <TopContainer>
                    <TitleContainer>{title}</TitleContainer>
                    <StyledIcon iconName={iconName} />
                </TopContainer>
                {hasPopData ? (
                    <PopGridContainer>
                        <PreviousValueCell>
                            <ColorDot color={orange[400]} />
                            <PopValueText>{previousValue}</PopValueText>
                        </PreviousValueCell>
                        <CurrentValueCell>
                            <ColorDot color={blue[400]} />
                            <PopValueText>{valNum}</PopValueText>
                        </CurrentValueCell>
                        <ChangeCell>
                            <ChangeValue
                                value={changeFilter()(change)}
                                isDecrease={change < 0}
                                unsigned={change === 0}
                            />
                        </ChangeCell>
                    </PopGridContainer>
                ) : (
                    <ValContainer>{valNum}</ValContainer>
                )}
            </CubeWrapper>
        </Tooltip>
    );
};

export default Cube;
